<script setup lang="ts">
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Loader2 } from 'lucide-vue-next'
import { zmgPublicCacheClient, zmgAdminOnlyCacheUpdateClient } from '@api-client/zmg_cache'
import { onMounted, ref } from 'vue'
import { toast } from "@iframe-widgets/pages/widgets/toast/iframeToast"
import type { DateRangeValueType } from "@lib/zod/dateTimePropEditor"
import { DEFAULT_HOLIDAY_NOTICE } from "@lib/zod/dateTimePropEditor"
import DateRangeListEditor from "@/ui-plus/date-time-prop-editor/DateRangeListEditor.vue"

const props = defineProps<{
    storeId: string
}>()

const emit = defineEmits<{
    (e: 'saved'): void
}>()

const NOTICE_MAX = 80

const isLoading = ref(false)
const isSaving = ref(false)
const isEnabled = ref(false)
const textNotice = ref('')
const dateRanges = ref<DateRangeValueType[]>([])

const loadData = async () => {
    isLoading.value = true
    const { data, error } = await (await zmgPublicCacheClient.store.holiday_notice[':id']['data.json'].$get({
        param: {
            id: props.storeId,
        },
    })).json()
    if (error) {
        toast.error(error.message || 'Failed to load holiday notice')
    } else if (data) {
        isEnabled.value = data.isEnabled ?? false
        textNotice.value = data.textNotice ?? ''
        dateRanges.value = data.dateRanges ?? []
    } else {
        isEnabled.value = false
        textNotice.value = ''
        dateRanges.value = []
    }
    isLoading.value = false
}

onMounted(() => {
    loadData()
})

async function saveHolidayNotice() {
    if (!(import.meta.env.DEV || prompt("please enter '123' to save holiday / close store") === "123")) {
        toast.error("wrong password")
        return
    }
    isSaving.value = true
    try {
        const trimmedNotice = textNotice.value.trim().slice(0, NOTICE_MAX)
        const { error } = await (await zmgAdminOnlyCacheUpdateClient.store.holiday_notice[':id'].$patch({
            param: {
                id: props.storeId,
            },
            json: {
                isEnabled: isEnabled.value,
                textNotice: trimmedNotice || null,
                dateRanges: dateRanges.value.length > 0 ? dateRanges.value : null,
            },
        })).json()
        if (error) {
            toast.error(error.message || 'Failed to save')
            return
        }
        textNotice.value = trimmedNotice
        emit('saved')
        toast.success('Saved')
    } catch {
        toast.error('Failed to save')
    } finally {
        isSaving.value = false
    }
}
</script>

<template>
    <div class="space-y-6">
        <div v-if="isLoading"
             class="flex justify-center py-8">
            <Loader2 class="h-6 w-6 animate-spin" />
        </div>
        <template v-else>
            <div class="flex items-center justify-between gap-4">
                <div class="space-y-1">
                    <Label for="holiday-enabled">Temporarily closed / holiday</Label>
                    <p class="text-muted-foreground text-xs">
                        When on, buyers see a warning and cannot place orders.
                        If no date range is set, it stays on until you turn it off.
                    </p>
                </div>
                <Switch id="holiday-enabled"
                        :checked="isEnabled"
                        @update:checked="isEnabled = $event" />
            </div>

            <div v-if="isEnabled">
                <div class="space-y-2">
                    <Label for="holiday-notice">Buyer message (max {{ NOTICE_MAX }})</Label>
                    <Input id="holiday-notice"
                           v-model="textNotice"
                           :maxlength="NOTICE_MAX"
                           :placeholder="DEFAULT_HOLIDAY_NOTICE" />
                    <p class="text-muted-foreground text-xs">{{ textNotice.length }}/{{ NOTICE_MAX }}</p>
                </div>

                <br>
                <br>

                <div class="space-y-2">
                    <Label>Schedule (optional)</Label>
                    <p class="text-muted-foreground text-xs">
                        Leave empty to apply now. Add ranges to schedule ahead.
                    </p>
                    <DateRangeListEditor v-model="dateRanges" />
                </div>
            </div>

            <Button class="w-full"
                    @click="saveHolidayNotice"
                    :disabled="isSaving">
                <Loader2 v-if="isSaving"
                         class="mr-2 h-4 w-4 animate-spin" />
                Save
            </Button>
        </template>
    </div>
</template>
